import { computed, inject, Injectable, Signal } from '@angular/core';
import { ApiListTransitionStoryGroupsResponse, ApiStoryResponse, ApiWatchlistResponse } from '../../api/core/models';
import { TransitionElement } from '../../model/enums/transition-element';
import { CreateLegacyCustomElement, TransitionStoriesStore } from './transition-stories.store';

@Injectable({ providedIn: 'root' })
export class TransitionStoriesFacadeService {
  private readonly _store = inject(TransitionStoriesStore);

  error = this._store.error;
  isLoading = this._store.isLoading;
  isSaving = this._store.isSaving;
  transitionStories = this._store.transitionHierarchyStories;
  transitionStoryHierarchy = this._store.transitionStoryHierarchy;

  createElements(
    parentGroupId: string,
    transitionElements: { title: string; transitionElement: TransitionElement }[],
    legacyCustomElements: CreateLegacyCustomElement[]
  ) {
    this._store.createElements({ parentGroupId, transitionElements, legacyCustomElements });
  }

  createLegacyCustomElementStory(id: string, story: ApiStoryResponse) {
    this._store.createLegacyCustomElementStory({ id, story });
  }

  createTransitionElementStory(id: string, story: ApiStoryResponse) {
    this._store.createTransitionElementStory({ id, story });
  }

  createTransitionStoryGroup(newGroupId: string, parentId?: string) {
    this._store.createTransitionStoryGroup({ newGroupId, parentGroupId: parentId });
  }

  createTransitionStoryGroupStory(id: string, story: ApiStoryResponse) {
    this._store.createTransitionStoryGroupStory({ id, story });
  }

  deleteLegacyCustomElement(id: string) {
    this._store.deleteLegacyCustomElement(id);
  }

  deleteTransitionElement(id: string) {
    this._store.deleteTransitionElement(id);
  }

  deleteTransitionStoryGroup(id: string) {
    this._store.deleteTransitionStoryGroup(id);
  }

  getLegacyCustomElementStory(id: string): Signal<ApiStoryResponse | undefined> {
    if (!this.transitionStories()[id]) {
      this._store.loadLegacyCustomElementStory(id);
    }
    return computed(() => Object.freeze(this.transitionStories()[id]));
  }

  getTransitionElementStory(id: string): Signal<ApiStoryResponse | undefined> {
    if (!this.transitionStories()[id]) {
      this._store.loadTransitionElementStory(id);
    }
    return computed(() => Object.freeze(this.transitionStories()[id]));
  }

  getTransitionStoryGroupStory(id: string): Signal<ApiStoryResponse | undefined> {
    if (!this.transitionStories()[id]) {
      this._store.loadTransitionStoryGroupStory(id);
    }

    return computed(() => Object.freeze(this.transitionStories()[id]));
  }

  loadTransitionStoryHierarchy() {
    if (!this._store.transitionStoryHierarchy()) {
      this._store.loadTransitionStoryHierarchy();
    }
    return computed(() => Object.freeze(this._store.transitionStoryHierarchy()));
  }

  loadStoriesForFullTransitionHierarchy(): Signal<{
    hierarchy: ApiListTransitionStoryGroupsResponse | undefined;
    isFull: boolean;
  }> {
    if (!this._store.isFullTransitionStoryHierarchy()) {
      this._store.loadAllStories();
    }
    return computed(() =>
      Object.freeze({
        hierarchy: this._store.transitionStoryHierarchy(),
        isFull: this._store.isFullTransitionStoryHierarchy(),
      })
    );
  }

  moveLegacyCustomElement(id: string, targetGroupId: string, targetOrder: number) {
    this._store.moveLegacyCustomElement({ id, targetGroupId, targetOrder });
  }

  moveTransitionElement(id: string, targetGroupId: string, targetOrder: number) {
    this._store.moveTransitionElement({ id, targetGroupId, targetOrder });
  }

  moveTransitionStoryGroup(id: string, targetOrder: number, targetParentGroupId?: string) {
    this._store.moveTransitionStoryGroup({ id, targetOrder, targetParentGroupId });
  }

  updateLegacyCustomElement(id: string, title?: string, data?: any) {
    this._store.updateLegacyCustomElement({ id, title, data });
  }

  updateLegacyCustomElementStory(id: string, story: ApiStoryResponse) {
    this._store.updateLegacyCustomElementStory({ id, story });
  }

  updateLegacyCustomElementWatchlist(id: string, watchlist: ApiWatchlistResponse, shouldDeleteWatchlist: boolean) {
    this._store.updateLegacyCustomElementWatchlist({ id, watchlist, shouldDeleteWatchlist });
  }

  updateTransitionElementStory(id: string, story: ApiStoryResponse) {
    this._store.updateTransitionElementStory({ id, story });
  }

  updateTransitionStoryGroup(id: string, title: string) {
    this._store.updateTransitionStoryGroup({ id, title });
  }

  updateTransitionStoryGroupColour(id: string, colour: string) {
    this._store.updateTransitionStoryGroupColour({ id, colour });
  }

  updateTransitionStoryGroupStory(id: string, story: ApiStoryResponse) {
    this._store.updateTransitionStoryGroupStory({ id, story });
  }
}
